/**
 * A whole score, read back out of a saved file.
 *
 * Beyond the field-by-field reading, two whole-document conditions are checked here
 * because nothing smaller can see them: every part has to hold the same bars the timeline
 * does, and no id may name two things. Both are invariants the rest of the package relies
 * on without checking -- a lookup by id assumes there is at most one answer -- so a file
 * that breaks them is refused at the door rather than allowed in to misbehave later.
 */

import type { MeasureId, PartId } from "../document/ids";
import type { Duration } from "../document/duration";
import type {
  GlobalMeasure,
  Instrument,
  JumpMarks,
  KeySignature,
  Part,
  PartMeasure,
  RepeatMarks,
  Score,
  ScoreMeta,
  Staff,
  StaffGroup,
  Tempo,
  TimeSignature,
} from "../document/types";
import { STEPS } from "../document/pitch";
import type { DrumNote, PercussionKit } from "../document/percussion";
import type { StringTuning } from "../document/tab";
import { BAR_NUMBER_INTERVAL_MAX, DEFAULT_LAYOUT } from "../document/layout";
import type { BarNumbers, Layout } from "../document/layout";
import {
  readClef,
  readDuration,
  readPartMeasure,
  readSpanner,
  readTransposition,
} from "./read-events";
import {
  ReadFault,
  at,
  counted,
  field,
  flag,
  identifier,
  keyed,
  list,
  measured,
  maybe,
  oneOf,
  record,
  text,
  whole,
} from "./reader";

function readMeta(value: unknown, path: string): ScoreMeta {
  const line = (key: string): string | null => at(value, key, path, (v, p) => maybe(text, v, p));
  return {
    title: at(value, "title", path, text),
    subtitle: line("subtitle"),
    composer: line("composer"),
    arranger: line("arranger"),
    lyricist: line("lyricist"),
    copyright: line("copyright"),
    movementNumber: line("movementNumber"),
    movementTitle: line("movementTitle"),
  };
}

/**
 * The page, which a file written before there was one does not mention.
 *
 * An absent layout is A4 with a 7mm staff -- the page every score of that vintage was
 * drawn on, since the renderer was given those numbers and the document had no say. So
 * the default is not a guess at what the writer wanted; it is what they had.
 */
function readLayout(value: unknown, path: string): Layout {
  if (value === undefined || value === null) return DEFAULT_LAYOUT;
  const margins = at(value, "margins", path, (v, p) => ({
    left: at(v, "left", p, measured),
    right: at(v, "right", p, measured),
    top: at(v, "top", p, measured),
    bottom: at(v, "bottom", p, measured),
  }));
  return {
    staffHeight: at(value, "staffHeight", path, measured),
    pageWidth: at(value, "pageWidth", path, measured),
    pageHeight: at(value, "pageHeight", path, measured),
    margins,
    systemGap: at(value, "systemGap", path, measured),
    staffGap: at(value, "staffGap", path, measured),
    // A file written before parts could gather their rests says nothing about it, and a
    // score is the thing that does not gather them, so silence reads as off.
    multiRests: at(value, "multiRests", path, (v, p) => (v === undefined ? false : flag(v, p))),
    // Likewise a file written before a staff could be left out of a system: every staff
    // was drawn then, and that is what silence means now.
    hideEmptyStaves: at(value, "hideEmptyStaves", path, (v, p) =>
      v === undefined ? false : flag(v, p),
    ),
    barNumbers: at(value, "barNumbers", path, readBarNumbers),
  };
}

/**
 * How often the bars are numbered, which a file written before there was a choice does
 * not mention. Silence reads as the start of each system: that is what was drawn then,
 * because the renderer numbered them and the document had no say.
 */
function readBarNumbers(value: unknown, path: string): BarNumbers {
  if (value === undefined) return "system";
  if (typeof value !== "number") return oneOf<"none" | "system">(["none", "system"], value, path);
  const interval = counted(value, path);
  if (interval < 1 || interval > BAR_NUMBER_INTERVAL_MAX) {
    throw new ReadFault(path, `a bar count between 1 and ${BAR_NUMBER_INTERVAL_MAX}`);
  }
  return interval;
}

function readTime(value: unknown, path: string): TimeSignature {
  const beats = at(value, "beats", path, counted);
  const beatType = at(value, "beatType", path, counted);
  if (beats < 1 || beatType < 1) throw new RangeError(`${path} is not a metre`);
  return {
    beats,
    beatType,
    symbol: at(value, "symbol", path, (v, p) => oneOf(["number", "common", "cut"] as const, v, p)),
  };
}

function readKey(value: unknown, path: string): KeySignature {
  return {
    fifths: at(value, "fifths", path, whole),
    mode: at(value, "mode", path, (v, p) =>
      maybe((entry, where) => oneOf(["major", "minor"] as const, entry, where), v, p),
    ),
  };
}

function readTempo(value: unknown, path: string): Tempo {
  const unit: Duration = at(value, "unit", path, readDuration);
  return {
    bpm: at(value, "bpm", path, counted),
    unit,
    text: at(value, "text", path, (v, p) => maybe(text, v, p)),
  };
}

/**
 * A bar's repeat marks. A file written before a repeat could state its count says nothing of
 * `times`, and reads as stating none: the section is played as its ending numbers say.
 */
function readRepeat(value: unknown, path: string): RepeatMarks {
  return {
    start: at(value, "start", path, flag),
    end: at(value, "end", path, flag),
    endings: at(value, "endings", path, (v, p) => list(counted, v, p)),
    times: at(value, "times", path, (v, p) => maybe(counted, v, p)),
  };
}

/**
 * A bar's jumps, which a file written before jumps existed says nothing about.
 *
 * A missing field is not an error here: the bar had no jump then and has none now. It is
 * `maybe`'d by the caller, so this only runs on a bar that states one.
 */
function readJump(value: unknown, path: string): JumpMarks {
  return {
    segno: at(value, "segno", path, flag),
    coda: at(value, "coda", path, flag),
    toCoda: at(value, "toCoda", path, flag),
    fine: at(value, "fine", path, flag),
    jump: at(value, "jump", path, (v, p) =>
      maybe((entry, where) => oneOf(["daCapo", "dalSegno"] as const, entry, where), v, p),
    ),
  };
}

function readGlobalMeasure(value: unknown, path: string): GlobalMeasure {
  return {
    id: at(value, "id", path, identifier<"measure">),
    time: at(value, "time", path, (v, p) => maybe(readTime, v, p)),
    key: at(value, "key", path, (v, p) => maybe(readKey, v, p)),
    tempo: at(value, "tempo", path, (v, p) => maybe(readTempo, v, p)),
    barline: at(value, "barline", path, (v, p) =>
      oneOf(["regular", "double", "final", "dashed", "heavy", "none"] as const, v, p),
    ),
    repeat: at(value, "repeat", path, (v, p) => maybe(readRepeat, v, p)),
    jump: at(value, "jump", path, (v, p) => maybe(readJump, v, p)),
    rehearsal: at(value, "rehearsal", path, (v, p) => maybe(text, v, p)),
    breakBefore: at(value, "breakBefore", path, (v, p) =>
      maybe((entry, where) => oneOf(["system", "page"] as const, entry, where), v, p),
    ),
    // A file written before pickups existed says nothing here, and a bar that says nothing
    // is a bar as long as its metre -- which is what every bar of those files was.
    length: at(value, "length", path, (v, p) => maybe(counted, v, p)),
  };
}

function readDrum(value: unknown, path: string): DrumNote {
  // A kit written before the open hi-hat could be told from the closed one says nothing
  // here, and a drum that is not marked open is struck and damped -- which is what every
  // drum of those kits was.
  const openField = field(value, "open", path);
  return {
    name: at(value, "name", path, text),
    midi: at(value, "midi", path, counted),
    step: at(value, "step", path, (v, p) => oneOf(STEPS, v, p)),
    octave: at(value, "octave", path, whole),
    notehead: at(value, "notehead", path, (v, p) =>
      oneOf(["normal", "x", "circle-x", "diamond", "triangle", "slash"] as const, v, p),
    ),
    open: openField === undefined ? false : flag(openField, `${path}.open`),
    stem: at(value, "stem", path, (v, p) => oneOf(["auto", "up", "down"] as const, v, p)),
  };
}

function readKit(value: unknown, path: string): PercussionKit {
  return {
    name: at(value, "name", path, text),
    notes: at(value, "notes", path, (v, p) => list(readDrum, v, p)),
  };
}

function readInstrument(value: unknown, path: string): Instrument {
  // A file written before drums existed says nothing about a kit, and a part without one
  // is a part with pitches -- which is what every part of those files was.
  const kit = field(value, "percussion", path);
  const tuning = field(value, "tuning", path);
  const printing = field(value, "keyPrinting", path);
  return {
    name: at(value, "name", path, text),
    sound: at(value, "sound", path, (v, p) => maybe(text, v, p)),
    midiProgram: at(value, "midiProgram", path, (v, p) => maybe(whole, v, p)),
    transposition: at(value, "transposition", path, readTransposition),
    percussion: kit === undefined ? null : maybe(readKit, kit, `${path}.percussion`),
    // Likewise a file written before tablature: a part with no tuning has no strings to
    // count frets from, which is what a part written on pitches is.
    tuning: tuning === undefined ? null : maybe(readTuning, tuning, `${path}.tuning`),
    // A file written before a part could spell its own signature says nothing about it,
    // and every part of those files printed the derived one.
    keyPrinting:
      printing === undefined
        ? "derived"
        : oneOf(["derived", "none", "sharps", "flats"] as const, printing, `${path}.keyPrinting`),
  };
}

function readTuning(value: unknown, path: string): readonly StringTuning[] {
  return list(
    (entry, at_) => ({
      step: at(entry, "step", at_, (v, p) => oneOf(STEPS, v, p)),
      alter: at(entry, "alter", at_, whole),
      octave: at(entry, "octave", at_, whole),
    }),
    value,
    path,
  );
}

function readStaff(value: unknown, path: string): Staff {
  const lines = at(value, "lines", path, counted);
  if (lines < 1) throw new RangeError(`${path}.lines is not a staff`);
  return {
    id: at(value, "id", path, identifier<"staff">),
    clef: at(value, "clef", path, readClef),
    lines,
  };
}

function readGroup(value: unknown, path: string): StaffGroup {
  return {
    id: at(value, "id", path, identifier<"group">),
    symbol: at(value, "symbol", path, (v, p) =>
      oneOf(["bracket", "brace", "line", "none"] as const, v, p),
    ),
    barlineSpan: at(value, "barlineSpan", path, flag),
    name: at(value, "name", path, (v, p) => maybe(text, v, p)),
    parts: at(value, "parts", path, (v, p) => list(identifier<"part">, v, p)),
  };
}

function readPart(value: unknown, path: string): Part {
  const staves = at(value, "staves", path, (v, p) => list(readStaff, v, p));
  if (staves.length === 0) throw new RangeError(`${path}.staves is empty`);
  const measures = at(value, "measures", path, (v, p) => keyed(readPartMeasure, v, p));

  return {
    id: at(value, "id", path, identifier<"part">),
    name: at(value, "name", path, text),
    abbreviation: at(value, "abbreviation", path, text),
    instrument: at(value, "instrument", path, readInstrument),
    staves,
    measures: measures as Readonly<Record<MeasureId, PartMeasure>>,
    spanners: at(value, "spanners", path, (v, p) => list(readSpanner, v, p)),
  };
}

/** Every id in a document, refusing the first one that names two things. */
function checkIds(score: Score): void {
  const seen = new Set<string>();
  const claim = (id: string): void => {
    if (seen.has(id)) throw new RangeError(`the id ${id} names more than one thing`);
    seen.add(id);
  };

  for (const measure of score.measures) claim(measure.id);
  for (const group of score.groups) claim(group.id);
  for (const part of score.parts) {
    claim(part.id);
    for (const staff of part.staves) claim(staff.id);
    for (const spanner of part.spanners) claim(spanner.id);
    for (const measure of Object.values(part.measures)) {
      for (const direction of measure.directions) claim(direction.id);
      for (const voice of measure.voices) {
        claim(voice.id);
        for (const event of voice.events) claim(event.id);
      }
    }
  }
}

/** Every part holds exactly the bars the timeline has, no more and no fewer. */
function checkBars(score: Score): void {
  const timeline = score.measures.map((measure) => measure.id);
  const known = new Set<string>(timeline);
  for (const part of score.parts) {
    for (const id of Object.keys(part.measures)) {
      if (!known.has(id)) throw new RangeError(`${part.name} has a bar the piece does not have`);
    }
    for (const id of timeline) {
      if (part.measures[id] === undefined) {
        throw new RangeError(`${part.name} is missing a bar the piece has`);
      }
    }
  }
}

/** A whole score. Throws a {@link ReadFault} or a `RangeError` on anything unreadable. */
export function readScore(value: unknown, path = ""): Score {
  record(value, path);
  const measures = at(value, "measures", path, (v, p) => list(readGlobalMeasure, v, p));
  if (measures.length === 0) throw new RangeError("a score has at least one bar");

  const score: Score = {
    id: at(value, "id", path, identifier<"score">),
    meta: at(value, "meta", path, readMeta),
    layout: readLayout(field(value, "layout", path), `${path}.layout`),
    measures,
    parts: at(value, "parts", path, (v, p) => list(readPart, v, p)),
    groups: at(value, "groups", path, (v, p) => list(readGroup, v, p)),
  };

  checkIds(score);
  checkBars(score);
  const parts = new Set<PartId>(score.parts.map((part) => part.id));
  for (const group of score.groups) {
    for (const id of group.parts) {
      if (!parts.has(id)) throw new RangeError(`a group brackets a part that is not there`);
    }
  }
  return score;
}
